import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Bar,
  BarChart,
  Rectangle,
  Tooltip,
} from 'recharts';
import {
  formatCurrency,
  formatNumberCommas,
} from '../../../../../../../utils/CurrencyUtils';
import { useScreenView } from '../../../../../../../utils/ScreenViewContext';
import { CostRevenuePerMileData } from '../../../../../../../types/apiTypes';
import ClipLoader from 'react-spinners/ClipLoader';
import { DemoDataWrapper } from '../../../home/components/DemoDataBadge';

function CostRevenuePerMileWidget({
  cpmRpmData,
  loadingCpmRpmData,
  milesDriven,
  selectedUnitNumber,
  isPreActive,
}: {
  cpmRpmData: CostRevenuePerMileData[];
  milesDriven: number;
  loadingCpmRpmData: boolean;
  selectedUnitNumber: string | undefined;
  isPreActive: boolean;
}) {
  const { screenView } = useScreenView();
  const averageCpm =
    cpmRpmData?.reduce((acc, curr) => acc + curr?.cpm, 0) / cpmRpmData?.length;
  const averageRpm =
    cpmRpmData?.reduce((acc, curr) => acc + curr?.rpm, 0) / cpmRpmData?.length;

  const CustomTooltip = ({ active, payload, label }: any) => {
    const date = new Date(0); // Create a date object
    date.setUTCMonth(label); // Set the month (0-based index)
    const month = date.toLocaleString('default', { month: 'short' });
    if (active && payload && payload?.length) {
      return (
        <div className="rounded-lg bg-gray-800 p-3 text-white shadow-lg">
          <p className="text-[14px] font-semibold">{month}.</p>
          <div className="flex items-center">
            <span
              className="mr-2 inline-block h-3 w-3"
              style={{ backgroundColor: '#EF4444' }}
            ></span>
            <p className="text-[14px]">
              CPM: ${payload[0]?.value?.toLocaleString()}
            </p>
          </div>
          <div className="flex items-center">
            <span
              className="mr-2 inline-block h-3 w-3"
              style={{ backgroundColor: '#6A9E68' }}
            ></span>
            <p className="text-[14px]">
              RPM: ${payload[1]?.value?.toLocaleString()}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
        return (
          <DemoDataWrapper showBadge={isPreActive}>
            <div className="flex w-[100%] flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle">
              <div className="flex h-full w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
                <h2 className="flex items-center text-[16px] font-semibold leading-[24px] text-primary">
                  Cost per Mile vs Revenue per Mile
                  {selectedUnitNumber && (
                    <span className="ml-2 block text-sm font-normal text-graysecondary">
                      {selectedUnitNumber}
                    </span>
                  )}
                </h2>
                <div className="flex flex-row items-center space-x-4 align-middle">
                  <div className="h-4 w-4 rounded-sm bg-secondary"></div>
                  <p>Revenue per mile</p>
                  <div className="h-4 w-4 rounded-sm bg-red-500"></div>
                  <p>Cost per mile</p>
                </div>
              </div>
              {!loadingCpmRpmData && (
                <div className="flex w-full flex-col items-start overflow-x-auto border-b-[1px] border-[#EAECF0] align-middle lg:h-[90px] lg:flex-row">
                  <div className="flex w-full flex-col items-start gap-y-2 border-b-[1px] p-3 align-middle lg:w-[33.3%] lg:border-none">
                    <p className="text-[16px] text-[#7D8191]">
                      {' '}
                      Average Cost per Mile{' '}
                    </p>
                    <p className="text-[22px] font-semibold leading-[28px] text-primary">
                      {formatCurrency(averageCpm)}
                    </p>
                  </div>
                  <div className="flex w-full flex-col items-start gap-y-2 border-b-[1px] border-[#EAECF0] p-3 align-middle lg:w-[33.3%] lg:border-b-0 lg:border-l-[1px]">
                    <p className="text-[16px] text-[#7D8191]">
                      {' '}
                      Average Revenue per Mile{' '}
                    </p>
                    <p className="text-[22px] font-semibold leading-[28px] text-primary">
                      {formatCurrency(averageRpm)}
                    </p>
                  </div>
                  <div className="flex w-full flex-col items-start gap-y-2 border-b-[1px] border-[#EAECF0] p-3 align-middle lg:w-[33.3%] lg:border-b-0 lg:border-l-[1px]">
                    <p className="text-[16px] text-[#7D8191]"> Miles Driven </p>
                    <p className="text-[22px] font-semibold leading-[28px] text-primary">
                      {formatNumberCommas(milesDriven)}
                    </p>
                  </div>
                </div>
              )}
              {!loadingCpmRpmData ? (
                <div className="h-[300px] min-h-[300px] w-full py-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      width={500}
                      height={300}
                      data={cpmRpmData}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="month"
                        tickFormatter={(monthIndex) => {
                          const date = new Date(0); // Create a date object
                          date.setUTCMonth(monthIndex); // Set the month (0-based index)
                          return date.toLocaleString('default', {
                            month: 'short',
                          }); // Format to short month
                        }}
                      />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar
                        dataKey="cpm"
                        fill="#EF4444"
                        activeBar={<Rectangle fill="red" stroke="black" />}
                      />
                      <Bar
                        dataKey="rpm"
                        fill="#6A9E68"
                        activeBar={<Rectangle fill="green" stroke="black" />}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className={`h-[390px] min-h-[390px]`}>
                  <ClipLoader
                    color="#1D213B"
                    loading={loadingCpmRpmData}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="my-10 flex h-full w-full items-center justify-center"
                  />
                </div>
              )}
            </div>
          </DemoDataWrapper>
        );
      case 'mobile':
        return (
          <DemoDataWrapper showBadge={isPreActive}>
            <div className="flex w-[100%] flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle">
              <div className="flex h-full w-full flex-col justify-between border-b-[1px] border-bordergray p-4">
                <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                  Cost per Mile vs Revenue per Mile
                  {selectedUnitNumber && (
                    <span className="block text-sm font-normal text-graysecondary">
                      {selectedUnitNumber}
                    </span>
                  )}
                </h2>
                <div className="flex flex-row items-center space-x-4 align-middle">
                  <div className="h-4 w-4 rounded-sm bg-secondary"></div>
                  <p>Revenue per mile</p>
                  <div className="h-4 w-4 rounded-sm bg-red-500"></div>
                  <p>Cost per mile</p>
                </div>
              </div>
              {!loadingCpmRpmData && (
                <div className="flex w-full flex-col items-start overflow-x-auto border-b-[1px] border-[#EAECF0] align-middle">
                  <div className="flex w-full flex-col items-start gap-y-2 border-b-[1px] border-bordergray p-3 align-middle">
                    <p className="text-[16px] text-[#7D8191]">
                      {' '}
                      Cost per mile{' '}
                    </p>
                    <p className="text-[22px] font-semibold leading-[28px] text-primary">
                      {formatCurrency(averageCpm)}
                    </p>
                  </div>
                  <div className="flex w-full flex-col items-start gap-y-2 border-b-[1px] border-l-[1px] border-[#EAECF0] p-3 align-middle">
                    <p className="text-[16px] text-[#7D8191]">
                      {' '}
                      Revenue per mile{' '}
                    </p>
                    <p className="text-[22px] font-semibold leading-[28px] text-primary">
                      {formatCurrency(averageRpm)}
                    </p>
                  </div>
                  <div className="flex w-full flex-col items-start gap-y-2 border-b-[1px] border-l-[1px] border-[#EAECF0] p-3 align-middle">
                    <p className="text-[16px] text-[#7D8191]"> Miles Driven </p>
                    <p className="text-[22px] font-semibold leading-[28px] text-primary">
                      {formatNumberCommas(milesDriven)}
                    </p>
                  </div>
                </div>
              )}
              {!loadingCpmRpmData ? (
                <div className="h-[300px] min-h-[300px] w-full py-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      width={500}
                      height={300}
                      data={cpmRpmData}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="month"
                        tickFormatter={(monthIndex) => {
                          const date = new Date(0); // Create a date object
                          date.setUTCMonth(monthIndex); // Set the month (0-based index)
                          return date.toLocaleString('default', {
                            month: 'short',
                          }); // Format to short month
                        }}
                      />
                      <YAxis />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar
                        dataKey="cpm"
                        fill="#EF4444"
                        activeBar={<Rectangle fill="red" stroke="black" />}
                      />
                      <Bar
                        dataKey="rpm"
                        fill="#6A9E68"
                        activeBar={<Rectangle fill="green" stroke="black" />}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className={`h-[390px] min-h-[390px]`}>
                  <ClipLoader
                    color="#1D213B"
                    loading={loadingCpmRpmData}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="my-10 flex h-full w-full items-center justify-center"
                  />
                </div>
              )}
            </div>
          </DemoDataWrapper>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default CostRevenuePerMileWidget;
